/**
 * AG Grid's value-change flash timing: the highlight eases in, holds, then eases out as the delta
 * disappears. The box takes the design system's rounded corners and the delta stands half a digit off.
 */
const FADE_IN_MS = 100;
const HOLD_MS = 2000;
const FADE_OUT_MS = 1000;
const BOX_PADDING = 5;
const BOX_RADIUS = 4;
const DELTA_GAP_DIGITS = 0.5;
const AG_COLORS = { highlight: "rgba(22, 160, 133, 0.5)", up: "#43a047", down: "#e53935" };
const ARROWS = { up: "↑", down: "↓" };
/** CSS `ease`, cubic-bezier(0.25, 0.1, 0.25, 1). */
const ease = (progress) => {
    if (progress <= 0)
        return 0;
    if (progress >= 1)
        return 1;
    const bezier = (t, a, b) => 3 * a * t * (1 - t) ** 2 + 3 * b * t ** 2 * (1 - t) + t ** 3;
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        if (bezier(mid, 0.25, 0.25) < progress)
            lo = mid;
        else
            hi = mid;
    }
    return bezier((lo + hi) / 2, 0.1, 1);
};
export const flashAlpha = ({ start, from }, now) => {
    const t = now - start;
    if (t < FADE_IN_MS)
        return from + (1 - from) * ease(Math.max(0, t) / FADE_IN_MS);
    if (t < HOLD_MS)
        return 1;
    if (t < HOLD_MS + FADE_OUT_MS)
        return 1 - ease((t - HOLD_MS) / FADE_OUT_MS);
    return 0;
};
/** A new change restarts the timer; a highlight already showing continues from its current strength. */
export const startFlash = (previous, now) => ({
    start: now,
    from: previous ? flashAlpha(previous, now) : 0,
});
export const flashEnded = (flash, now) => now - flash.start >= HOLD_MS + FADE_OUT_MS;
export const flashColors = (families = {}) => ({
    highlight: families.highlight ? `var(--colour-surface-${families.highlight}-tonal)` : AG_COLORS.highlight,
    up: families.up ? `var(--colour-text-${families.up}-default)` : AG_COLORS.up,
    down: families.down ? `var(--colour-text-${families.down}-default)` : AG_COLORS.down,
});
/** The value text's box, laid out as Glide draws the text; the highlight pads it by a pixel each side. */
const textBox = (ctx, { rect, text, align, padding, font, bias }) => {
    ctx.font = font;
    ctx.textBaseline = "middle";
    const metrics = ctx.measureText(text);
    const left = align === "right"
        ? rect.x + rect.width - (padding + 0.5) - metrics.width
        : align === "center"
            ? rect.x + rect.width / 2 - metrics.width / 2
            : rect.x + padding + 0.5;
    const middle = rect.y + rect.height / 2 + bias;
    return {
        x: left - BOX_PADDING,
        y: middle - metrics.fontBoundingBoxAscent,
        width: metrics.width + BOX_PADDING * 2,
        height: metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent,
        middle,
    };
};
/** The highlight goes behind the cell's text, the delta in front of it, so each is its own layer. */
export const paintFlash = (ctx, target, flash, now, colors, layer) => {
    if (layer === "highlight") {
        const alpha = flashAlpha(flash, now);
        if (alpha <= 0)
            return;
        const box = textBox(ctx, target);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = colors.highlight;
        ctx.beginPath();
        ctx.roundRect(box.x, box.y, box.width, box.height, BOX_RADIUS);
        ctx.fill();
        ctx.restore();
        return;
    }
    if (!flash.delta || now - flash.start >= HOLD_MS)
        return;
    const box = textBox(ctx, target);
    ctx.save();
    ctx.fillStyle = colors[flash.delta.direction];
    ctx.textAlign = "right";
    const gap = ctx.measureText("0").width * DELTA_GAP_DIGITS;
    ctx.fillText(`${ARROWS[flash.delta.direction]}${flash.delta.text}`, box.x - gap, box.middle);
    ctx.restore();
};
//# sourceMappingURL=cellFlash.js.map